import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { BlogProvider } from './components/BlogContext';
import { SiteProvider } from './content/SiteContext';
import CmsPage from './pages/CmsPage';
import { AdminProvider } from './components/AdminContext';
import { ApplicationsProvider } from './components/ApplicationsContext';
import { FleetRequestsProvider } from './components/FleetRequestsContext';
import Header from './components/Header';
import Footer from './components/Footer';
import StickyCTA from './components/StickyCTA';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Pricing from './pages/Pricing';
import HowItWorks from './pages/HowItWorks';
import Membership from './pages/Membership';
import Corporate from './pages/Corporate';
import Locations from './pages/Locations';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Partner from './pages/Partner';
import Book from './pages/Book';
import Legal from './pages/Legal';
import NotFound from './pages/NotFound';
import MobileCarWashDubai from './pages/MobileCarWashDubai';
import MobileCarWashAbuDhabi from './pages/MobileCarWashAbuDhabi';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CarWashApp from './pages/CarWashApp';

// Admin/editor screens are never visited by customers — keep them out of the
// public bundle so public pages load and hydrate faster (Core Web Vitals).
const AdminApp = lazy(() => import('./admin/AdminApp'));
const BlogPostEditor = lazy(() => import('./components/BlogPostEditor'));
const BlogAdmin = lazy(() => import('./pages/BlogAdmin'));
const AdminApplications = lazy(() => import('./pages/AdminApplications'));
const AdminFleetRequests = lazy(() => import('./pages/AdminFleetRequests'));

// Public site chrome is hidden on /admin — the CMS has its own layout.
const Chrome = ({ children }) => {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin') ? null : children;
};

function App() {
  return (
    <HelmetProvider>
      <SiteProvider>
      <AdminProvider>
        <ApplicationsProvider>
          <FleetRequestsProvider>
            <BlogProvider>
              <Router>
                <ScrollToTop />
                <Chrome><Header /></Chrome>
                <main id="main" tabIndex="-1">
                  <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/services/:slug" element={<ServiceDetail />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/membership" element={<Membership />} />
                    <Route path="/corporate-fleet-car-wash" element={<Corporate />} />
                    <Route path="/locations" element={<Locations />} />
                    <Route path="/mobile-car-wash-abu-dhabi" element={<MobileCarWashAbuDhabi />} />
                    <Route path="/mobile-car-wash-dubai" element={<MobileCarWashDubai />} />
                    <Route path="/book" element={<Book />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/partner" element={<Partner />} />
                    <Route path="/car-wash-app" element={<CarWashApp />} />
                    <Route path="/privacy-policy" element={<Legal doc="privacy" />} />
                    <Route path="/terms" element={<Legal doc="terms" />} />
                    <Route path="/cancellation-policy" element={<Legal doc="cancellation" />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/create" element={<BlogPostEditor />} />
                    <Route path="/blog/edit/:id" element={<BlogPostEditor />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/admin/blog" element={<BlogAdmin />} />
                    <Route path="/admin/applications" element={<AdminApplications />} />
                    <Route path="/admin/fleet" element={<AdminFleetRequests />} />
                    <Route path="/admin/*" element={<AdminApp />} />
                    <Route path="/:slug" element={<CmsPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </main>
                <Chrome><Footer /></Chrome>
                <Chrome><StickyCTA /></Chrome>
              </Router>
            </BlogProvider>
          </FleetRequestsProvider>
        </ApplicationsProvider>
      </AdminProvider>
      </SiteProvider>
    </HelmetProvider>
  );
}

export default App;
